import { ComponentChildren } from 'preact';
import { SlideOver } from '@components/slide-over';
import { Button } from '@components/buttons';
import { IcoX } from '@components/icons';

/**
 * A right-side panel on desktop, a full-screen sheet on phone. The footer is
 * sticky inside the scrolling body, so it rides above the on-screen keyboard.
 */
export function Panel({
  title,
  header,
  footer,
  children,
  close,
}: {
  title: ComponentChildren;
  header?: ComponentChildren;
  footer?: ComponentChildren;
  children: ComponentChildren;
  close: () => void;
}) {
  return (
    <SlideOver close={close} w="w-screen sm:max-w-[440px]" p="p-0" hideCloseButton>
      <div class="flex flex-col min-h-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <header class="flex items-start gap-3 px-4 sm:px-6 pt-5 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex flex-col gap-2 grow min-w-0">
            <h2
              id="slide-over-title"
              class="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              {title}
            </h2>
            {header}
          </div>
          <Button
            class="shrink-0 p-2 -m-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 rounded"
            aria-label="Close"
            onClick={close}
          >
            <IcoX class="size-5" />
          </Button>
        </header>
        <div class="grow flex flex-col gap-6 px-4 sm:px-6 py-5">{children}</div>
        {footer && (
          <footer class="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 flex items-center gap-3">
            {footer}
          </footer>
        )}
      </div>
    </SlideOver>
  );
}

export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) {
    return;
  }
  return (
    <p id={id} class="text-sm text-red-600 dark:text-red-400">
      {message}
    </p>
  );
}

/** "Today" and "Yesterday" quick links beside a date input. */
export function QuickDates({
  today,
  yesterday,
  onPick,
}: {
  today: string;
  yesterday: string;
  onPick: (date: string) => void;
}) {
  return (
    <span class="flex gap-3">
      <Button
        class="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 underline text-sm"
        onClick={() => onPick(today)}
      >
        Today
      </Button>
      <Button
        class="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 underline text-sm"
        onClick={() => onPick(yesterday)}
      >
        Yesterday
      </Button>
    </span>
  );
}

export function StepHeading({ n, children }: { n: number; children: ComponentChildren }) {
  return (
    <h3 class="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
      <span class="inline-flex items-center justify-center size-6 rounded-full bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300">
        {n}
      </span>
      {children}
    </h3>
  );
}
